import { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  CalendarCheck,
  BookOpen,
  MessageSquare,
  MapPin,
  FolderOpen,
  FolderLock,
  FolderArchive,
  Receipt,
  ShieldCheck,
  ClipboardCheck,
  LogOut,
  Briefcase,
  Inbox,
  ChevronRight,
  KeyRound,
  Settings2,
  Utensils,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Sidebar nav is a TREE. Items with `children` render as an accordion: clicking
// the parent navigates AND expands; the chevron also lets the user collapse it.
// Sub-items are nested visually (indented + smaller icon) so the user sees them
// only when their parent section is open.
//
//   Parent App
//     └ Messages
//   Records              ← vault-locked
//   Staff Docs & Training ← vault-locked
//     └ Staff App
//   Licensing File       ← vault-locked
//     └ Playground       ← vault-locked
//   Compliance           ← vault-locked
const navTree = (role) => {
  const isOwner = role === "owner" || role === "superadmin";
  const tree = [
    { to: "/dashboard", label: "Overview", Icon: LayoutDashboard, end: true },
    { to: "/dashboard/children", label: "Children", Icon: Users },
    { to: "/dashboard/attendance", label: "Attendance", Icon: CalendarCheck },
    { to: "/dashboard/lesson-plans", label: "Lesson Plans", Icon: BookOpen },
    {
      to: "/dashboard/parent-app",
      label: "Parent App",
      Icon: MessageSquare,
      children: [
        { to: "/dashboard/messages", label: "Messages", Icon: Inbox },
      ],
    },
  ];
  if (isOwner) {
    tree.push(
      { to: "/dashboard/records", label: "Records", Icon: FolderArchive },
      {
        to: "/dashboard/staff-files",
        label: "Staffs File",
        Icon: FolderOpen,
        children: [
          { to: "/dashboard/staff", label: "Staff App", Icon: MapPin },
          { to: "/dashboard/staff/substitutes", label: "Substitutes", Icon: Briefcase },
        ],
      },
      {
        to: "/dashboard/licensing",
        label: "Licensing File",
        Icon: FolderLock,
        children: [
          { to: "/dashboard/drill-log", label: "Drill Log", Icon: ClipboardCheck },
          { to: "/dashboard/playground", label: "Playground", Icon: ClipboardCheck },
          { to: "/dashboard/licensing/food-menu", label: "Food Menu", Icon: Utensils },
        ],
      },
      { to: "/dashboard/compliance", label: "Compliance", Icon: ShieldCheck },
      { to: "/dashboard/settings", label: "Center Settings", Icon: Settings2 },
    );
  } else {
    // Staff & parents still need the Staff App for clock-in. Surface it
    // top-level since the owner file cabinets above don't exist for them.
    tree.push({ to: "/dashboard/staff", label: "Staff App", Icon: MapPin });
  }
  tree.push({ to: "/dashboard/billing", label: "Billing", Icon: Receipt });
  if (role === "admin") tree.push({ to: "/admin", label: "Admin · Leads", Icon: Briefcase });
  return tree;
};

const testId = (label) => `sidebar-link-${label.toLowerCase().replace(/[·&\s]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}`;

function NavBranch({ item, expanded, onToggle, location }) {
  const Icon = item.Icon;
  const hasChildren = !!(item.children && item.children.length);
  // Auto-expand when the user is on this section or any of its children.
  const isOnSection = location.pathname === item.to
    || (item.to !== "/dashboard" && location.pathname.startsWith(item.to + "/"))
    || (hasChildren && item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/")));
  const open = expanded || isOnSection;
  return (
    <div>
      <div className="relative">
        <NavLink
          to={item.to}
          end={item.end}
          data-testid={testId(item.label)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-emerald-50 text-emerald-800"
                : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
            } ${hasChildren ? "pr-9" : ""}`
          }
        >
          <Icon className="w-4 h-4" />
          {item.label}
        </NavLink>
        {hasChildren && (
          <button
            type="button"
            data-testid={`${testId(item.label)}-toggle`}
            onClick={(e) => { e.preventDefault(); onToggle(); }}
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-transform ${open ? "rotate-90" : ""}`}
            aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <div data-testid={`${testId(item.label)}-children`} className="ml-3 mt-1 mb-1 pl-3 border-l border-stone-200 space-y-1">
          {item.children.map((sub) => (
            <NavLink
              key={sub.to}
              to={sub.to}
              data-testid={testId(sub.label)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-800"
                    : "text-stone-500 hover:bg-stone-100 hover:text-stone-900"
                }`
              }
            >
              <sub.Icon className="w-3.5 h-3.5" />
              {sub.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const tree = navTree(user?.role);

  // User-driven expand state. Sections auto-expand when the user is browsing
  // inside them; this map only records manual toggles.
  const [openMap, setOpenMap] = useState({});
  // When the user navigates to a sub-route, ensure that section stays open
  // even if they later click a section toggle to manually close it.
  useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      for (const item of tree) {
        if (item.children) {
          const isInside = item.children.some(
            (c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"),
          );
          if (isInside) next[item.to] = true;
        }
      }
      return next;
    });
    // We intentionally only watch the pathname so this runs on every nav.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const doLogout = () => {
    logout();
    toast.success("Signed out");
    nav("/", { replace: true });
  };

  return (
    <div data-testid="dashboard-layout" className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-stone-200 bg-white">
        <Link to="/" className="h-16 flex items-center px-6 gap-2 border-b border-stone-200">
          <img src="/logo.svg" alt="ChildCheckOh" className="w-8 h-8" />
          <span className="font-display text-lg font-semibold tracking-tight text-stone-900">
            ChildCheck <span className="text-emerald-700">Ohio</span>
          </span>
        </Link>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {tree.map((item) => (
            <NavBranch
              key={item.to}
              item={item}
              expanded={!!openMap[item.to]}
              onToggle={() => setOpenMap((m) => ({ ...m, [item.to]: !m[item.to] }))}
              location={location}
            />
          ))}
        </nav>
        <div className="p-4 border-t border-stone-200">
          <div className="text-xs font-semibold text-stone-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[11px] text-stone-500 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          {/* iter-69b — "Vault settings" sidebar shortcut removed; the same
              controls now live inside Center Settings → Vault passcodes so
              the owner has a single page for everything. */}
          <Button
            data-testid="sidebar-logout-btn"
            variant="ghost"
            onClick={doLogout}
            className="mt-1 w-full justify-start text-stone-600 hover:text-stone-900"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-stone-200 z-40 flex items-center px-4 gap-3">
        <Link to="/" className="font-display font-semibold text-stone-900">ChildCheck</Link>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="font-semibold text-stone-900">{user?.name}</span>
          <button onClick={doLogout} className="text-stone-500"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>

      <main className="flex-1 min-w-0 lg:pl-0 pt-14 lg:pt-0">
        <div className="max-w-6xl mx-auto p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
